import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Users, Plus, Pencil, Trash2, Send, Sparkles, Cake, TrendingUp, Zap, RefreshCw, Gift } from "lucide-react";
import SegmentCouponAi from "../components/SegmentCouponAi";
import WaSendQueue from "../components/WaSendQueue";

const RULE_FIELDS = [
  { key: "min_points", label: "Punti minimi", type: "number", placeholder: "es. 100" },
  { key: "max_points", label: "Punti massimi", type: "number", placeholder: "es. 1000" },
  { key: "min_total_spent", label: "Speso minimo €", type: "number", placeholder: "es. 50" },
  { key: "max_total_spent", label: "Speso massimo €", type: "number", placeholder: "es. 500" },
  { key: "min_transactions", label: "N. min. acquisti", type: "number", placeholder: "es. 3" },
  { key: "last_purchase_days_max", label: "Attivi negli ultimi (giorni)", type: "number", placeholder: "es. 30" },
  { key: "last_purchase_days_min", label: "Dormienti da almeno (giorni)", type: "number", placeholder: "es. 60" },
  { key: "birthday_month", label: "Mese di compleanno (1-12)", type: "number", placeholder: "es. 12" },
];

const PRESETS = [
  { name: "VIP alto valore", rules: { min_total_spent: 200, min_transactions: 5 }, color: "#F59E0B", icon: Sparkles },
  { name: "Clienti attivi (30gg)", rules: { last_purchase_days_max: 30 }, color: "#10B981", icon: Zap },
  { name: "Clienti dormienti (60gg+)", rules: { last_purchase_days_min: 60 }, color: "#E5383B", icon: TrendingUp },
  { name: "Compleanni del mese", rules: { birthday_month: new Date().getMonth() + 1 }, color: "#EC4899", icon: Cake },
];

export default function Segments() {
  const [segments, setSegments] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [notifyFor, setNotifyFor] = useState(null);
  const [notifyBody, setNotifyBody] = useState("");
  const [notifyCoupon, setNotifyCoupon] = useState("none");
  const [waQueue, setWaQueue] = useState(null);
  const [couponAiFor, setCouponAiFor] = useState(null);

  const empty = { name: "", description: "", color: "#F59E0B", rules: {} };
  const [form, setForm] = useState(empty);
  const [vipCouponId, setVipCouponId] = useState("none");
  const [refreshing, setRefreshing] = useState(false);

  const loadVipCoupon = async () => {
    try {
      const { data } = await api.get("/segments/auto-vip/coupon");
      setVipCouponId(data.coupon_id || "none");
    } catch {}
  };
  const saveVipCoupon = async (v) => {
    setVipCouponId(v);
    try {
      await api.put("/segments/auto-vip/coupon", { coupon_id: v === "none" ? null : v });
      toast.success(v === "none" ? "Coupon VIP disattivato" : "Coupon VIP salvato");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const refreshVip = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/segments/auto-vip/refresh");
      toast.success(`Aggiornato · ${data.top} clienti · ${data.newcomers} nuovi · ${data.auto_assigned} coupon assegnati`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRefreshing(false); }
  };

  const load = async () => {
    const [s, c, b] = await Promise.all([
      api.get("/segments"),
      api.get("/coupons").catch(() => ({ data: [] })),
      api.get("/branches").catch(() => ({ data: [] })),
    ]);
    setSegments(s.data);
    setCoupons(c.data);
    setBranches(b.data);
  };
  useEffect(() => { load(); loadVipCoupon(); }, []);

  const submit = async () => {
    try {
      const clean = { ...form, rules: cleanRules(form.rules) };
      if (editing) await api.patch(`/segments/${editing.id}`, clean);
      else await api.post("/segments", clean);
      setOpen(false); toast.success("Cluster salvato"); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare il cluster?")) return;
    await api.delete(`/segments/${id}`); toast.success("Eliminato"); load();
  };
  const openEdit = (s) => { setEditing(s); setForm({ ...s }); setOpen(true); };
  const openNew = (preset) => {
    setEditing(null);
    setForm(preset ? { name: preset.name, description: "", color: preset.color, rules: preset.rules } : empty);
    setOpen(true);
  };
  const openPreview = async (s) => {
    setPreview({ ...s, members: [] });
    const { data } = await api.get(`/segments/${s.id}/customers`);
    setPreview({ ...s, members: data });
  };
  const openNotify = (s) => {
    setNotifyFor(s);
    setNotifyBody(`Ciao {{name}}! Un'offerta speciale del cluster "${s.name}": vieni a scoprirla al negozio 🍽️`);
    setNotifyCoupon("none");
  };
  const doNotify = async () => {
    try {
      const { data } = await api.post(`/segments/${notifyFor.id}/notify`, {
        body: notifyBody,
        coupon_id: notifyCoupon === "none" ? null : notifyCoupon,
      });
      toast.success(`${data.count} messaggi pronti`);
      data._queue = data.messages;
      setNotifyFor(null);
      setWaQueue({ messages: data.messages });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="segments-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-gold"><Users className="h-3 w-3" /> CLUSTER CLIENTI</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Segmenti dinamici</h1>
          <p className="text-zinc-400 mt-1">Crea gruppi in base ai comportamenti d'acquisto e lancia campagne mirate.</p>
        </div>
        <Button data-testid="segment-new" onClick={() => openNew(null)} className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] h-11">
          <Plus className="h-4 w-4 mr-2" /> Nuovo cluster
        </Button>
      </div>

      {/* Auto-VIP control */}
      <div className="card-elev p-5 glow-gold">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-amber-500/10 text-amber-300 flex items-center justify-center">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <div className="font-heading font-bold text-lg">Auto VIP · Top 20 (90gg)</div>
              <div className="text-xs text-zinc-400">Rigenerato ogni notte alle 03:00 UTC. Ricalcola al volo con il pulsante.</div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="flex items-center gap-2">
              <Gift className="h-4 w-4 text-amber-300" />
              <Select value={vipCouponId} onValueChange={saveVipCoupon}>
                <SelectTrigger data-testid="vip-coupon-select" className="bg-zinc-950 border-zinc-800 h-10 w-full sm:min-w-[220px]"><SelectValue placeholder="Regalo automatico" /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value="none">Nessun coupon automatico</SelectItem>
                  {coupons.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="vip-refresh" onClick={refreshVip} disabled={refreshing} className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Ricalcolo…" : "Ricalcola ora"}
            </Button>
          </div>
        </div>
      </div>

      {/* Presets */}
      <div className="card-elev p-4">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3 font-semibold">Modelli rapidi</div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.name}
              onClick={() => openNew(p)}
              className="pill hover:opacity-80 transition"
              style={{ background: `${p.color}20`, color: p.color, border: `1px solid ${p.color}40` }}
              data-testid={`preset-${p.name}`}
            >
              <p.icon className="h-3 w-3" /> {p.name}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {segments.length === 0 && <div className="text-zinc-500 col-span-full">Nessun cluster creato.</div>}
        {segments.map((s) => (
          <div key={s.id} className="card-elev p-5 flex flex-col gap-3" data-testid={`segment-${s.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                     style={{ background: `${s.color}20`, color: s.color, border: `1px solid ${s.color}40` }}>
                  <Users className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-lg truncate">{s.name}</div>
                  <div className="text-xs text-zinc-500 line-clamp-2">{s.description}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="metric-value text-2xl" style={{ color: s.color }}>{s.member_count}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">clienti</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 border-t border-zinc-900 pt-3 min-h-8">
              {renderRules(s.rules).map((r, i) => (
                <span key={i} className="pill pill-muted text-[10px]">{r}</span>
              ))}
            </div>
            <div className="flex gap-2">
              <Button data-testid={`segment-preview-${s.id}`} size="sm" variant="outline" onClick={() => openPreview(s)} className="border-zinc-800 flex-1">
                Membri
              </Button>
              <Button data-testid={`segment-notify-${s.id}`} size="sm" onClick={() => openNotify(s)} className="bg-[color:var(--whatsapp)] text-black hover:opacity-90 flex-1">
                <Send className="h-3.5 w-3.5 mr-1" /> Campagna
              </Button>
              <Button data-testid={`segment-coupon-ai-${s.id}`} size="sm" variant="outline" onClick={() => setCouponAiFor(s)} title="Coupon suggeriti dall'AI per questo cluster" className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10">
                <Sparkles className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => openEdit(s)} className="border-zinc-800"><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" onClick={() => remove(s.id)} className="border-zinc-800 hover:border-red-500 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      {couponAiFor && <SegmentCouponAi segment={couponAiFor} onClose={() => setCouponAiFor(null)} onCreated={load} />}

      {/* CREATE/EDIT */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica cluster" : "Nuovo cluster"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Label className="text-zinc-300 mb-1 inline-block">Nome</Label>
                <Input data-testid="segment-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-950 border-zinc-800" />
              </div>
              <div><Label className="text-zinc-300 mb-1 inline-block">Colore</Label>
                <div className="flex gap-2">
                  <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-14 rounded-md border border-zinc-800 bg-transparent cursor-pointer" />
                  <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="bg-zinc-950 border-zinc-800 font-mono text-xs" />
                </div>
              </div>
            </div>
            <div><Label className="text-zinc-300 mb-1 inline-block">Descrizione</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div className="border-t border-zinc-800 pt-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Regole di appartenenza</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {RULE_FIELDS.map((f) => (
                  <div key={f.key}>
                    <Label className="text-zinc-400 mb-1 inline-block text-xs">{f.label}</Label>
                    <Input
                      data-testid={`segment-rule-${f.key}`}
                      type={f.type}
                      placeholder={f.placeholder}
                      value={form.rules?.[f.key] ?? ""}
                      onChange={(e) => setForm({ ...form, rules: { ...form.rules, [f.key]: e.target.value === "" ? undefined : Number(e.target.value) } })}
                      className="bg-zinc-950 border-zinc-800"
                    />
                  </div>
                ))}
                {branches.length > 0 && (
                  <div className="sm:col-span-2">
                    <Label className="text-zinc-400 mb-1 inline-block text-xs">Sede</Label>
                    <Select value={form.rules?.branch_id || "all"} onValueChange={(v) => setForm({ ...form, rules: { ...form.rules, branch_id: v === "all" ? undefined : v } })}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800">
                        <SelectItem value="all">Tutte le sedi</SelectItem>
                        {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-zinc-500 mt-3">Le regole sono in AND: un cliente deve soddisfarle tutte per rientrare nel cluster.</p>
            </div>
            <Button data-testid="segment-form-submit" onClick={submit} className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
              {editing ? "Salva" : "Crea cluster"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PREVIEW MEMBERS */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Clienti nel cluster: {preview?.name}</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-1">
            {(preview?.members || []).length === 0 && <div className="text-sm text-zinc-500">Nessun cliente attualmente in questo cluster.</div>}
            {(preview?.members || []).map((c) => (
              <div key={c.id} className="p-2 rounded-md border border-zinc-900 flex justify-between items-center">
                <div>
                  <div className="text-sm font-medium">{c.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{c.card_number} · {c.phone}</div>
                </div>
                <span className="pill pill-gold font-mono">{c.points} pt</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* NOTIFY */}
      <Dialog open={!!notifyFor} onOpenChange={(o) => !o && setNotifyFor(null)}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Campagna sul cluster: {notifyFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Messaggio WhatsApp</Label>
              <textarea
                data-testid="segment-notify-body"
                value={notifyBody}
                onChange={(e) => setNotifyBody(e.target.value)}
                rows={4}
                className="w-full rounded-md p-2 bg-zinc-950 border border-zinc-800 text-sm"
              />
              <div className="text-[11px] text-zinc-500 mt-1">
                Variabili: <span className="font-mono">{"{{name}}"}</span>, <span className="font-mono">{"{{points}}"}</span>, <span className="font-mono">{"{{card}}"}</span>, <span className="font-mono">{"{{coupon}}"}</span>, <span className="font-mono">{"{{discount}}"}</span>, <span className="font-mono">{"{{coupon_expiry}}"}</span>
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Coupon da allegare (opzionale)</Label>
              <Select value={notifyCoupon} onValueChange={setNotifyCoupon}>
                <SelectTrigger data-testid="segment-notify-coupon" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value="none">Nessun coupon</SelectItem>
                  {coupons.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} · {c.code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-zinc-500 mt-1">Se scelto, il coupon viene auto-assegnato ai clienti del cluster.</div>
            </div>
            <Button data-testid="segment-notify-submit" onClick={doNotify} className="w-full bg-[color:var(--whatsapp)] text-black hover:opacity-90">
              <Send className="h-4 w-4 mr-2" /> Lancia campagna ({notifyFor?.member_count || 0} clienti)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <WaSendQueue open={!!waQueue} onClose={() => setWaQueue(null)} messages={waQueue?.messages || []} />
    </div>
  );
}

function cleanRules(r) {
  const out = {};
  Object.entries(r || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") out[k] = v; });
  return out;
}

function renderRules(rules) {
  const out = [];
  if (rules.min_points != null) out.push(`≥ ${rules.min_points} pt`);
  if (rules.max_points != null) out.push(`≤ ${rules.max_points} pt`);
  if (rules.min_total_spent != null) out.push(`speso ≥ € ${rules.min_total_spent}`);
  if (rules.max_total_spent != null) out.push(`speso ≤ € ${rules.max_total_spent}`);
  if (rules.min_transactions != null) out.push(`≥ ${rules.min_transactions} acquisti`);
  if (rules.last_purchase_days_max != null) out.push(`attivi ≤ ${rules.last_purchase_days_max}gg`);
  if (rules.last_purchase_days_min != null) out.push(`dormienti ≥ ${rules.last_purchase_days_min}gg`);
  if (rules.birthday_month != null) out.push(`nato mese ${rules.birthday_month}`);
  if (rules.branch_id) out.push(`sede specifica`);
  if (!out.length) out.push("Tutti i clienti");
  return out;
}
